if ( !customElements.get('scrolling-banner') ) {
  customElements.define('scrolling-banner', class ScrollingBanner extends HTMLElement {
    constructor() {
      super();

      this.isMobile = window.matchMedia(FoxTheme.config.mediaQueryMobile).matches;
      this.animationsInitialized = false;
      
      const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
      mql.onchange = () => {
        this.isMobile = mql.matches;
        this.cleanup();
        this.init();
      };
      
      this.init();
    }

    get items() {
      return this.querySelectorAll('.slide');
    }

    cleanup() {
      this.animationsInitialized = false;
      if (this.scrollAnimations) {
        this.scrollAnimations.forEach(animation => {
          if (animation && typeof animation.stop === 'function') {
            animation.stop();
          }
        });
        this.scrollAnimations = [];
      }
    }

    init() {
      this.zoom = this.dataset.zoom === 'true';
      this.parallax = this.dataset.parallax === 'true';
      this.header = document.querySelector('header');
      this.headerHeight = Math.round(this.header.offsetHeight);
      this.isHeaderSticky = this.header.isAlwaysSticky;
      this.borderRadius = this.dataset.borderRadius;
      this.scrollAnimations = [];

      // Completely disable both zoom and parallax on mobile
      if (this.isMobile) {
        this.zoom = false;
        this.parallax = false;
        
        // Reset any transforms and ensure cards show at full width on mobile
        [...this.items].forEach((item) => {
          const card = item.querySelector('.slide__card');
          const content = item.querySelector('.slide__content');
          const cardMedia = item.querySelector('.media');
          const product = item.querySelector('.slide__product');
          
          if (card) {
            card.style.clipPath = 'none';
            card.style.transform = 'none';
            card.style.willChange = 'auto';
          }
          if (content) {
            content.style.transform = 'none';
            content.style.willChange = 'auto';
          }
          if (cardMedia) {
            cardMedia.style.transform = 'none';
            cardMedia.style.willChange = 'auto';
          }
          if (product) {
            product.style.transform = 'none';
            product.style.willChange = 'auto';
          }
          
          // Also reset any child elements
          const cardMediaChild = cardMedia?.children[0];
          if (cardMediaChild) {
            cardMediaChild.style.transform = 'none';
            cardMediaChild.style.willChange = 'auto';
          }
        });
        
        this.slideRequestAnimationFrame = true;
        return; // Exit early on mobile
      }

      this.slideRequestAnimationFrame = true;
      this.resizeTimeout = null;

      this.onResizeHandler = (() => {
        clearTimeout(this.resizeTimeout);
        this.resizeTimeout = setTimeout(() => {
          if (this.slideRequestAnimationFrame) {
            this.slideRequestAnimationFrame = false;
            requestAnimationFrame(() => {
              this.cleanup();
              this.handleSlideAnimation();
            });
          }
        }, 200); // Increased debounce time
      }).bind(this);

      window.addEventListener("resize", this.onResizeHandler, { passive: true });

      if (!this.animationsInitialized) {
        this.handleSlideAnimation();
        this.animationsInitialized = true;
      }
    }

    handleSlideAnimation() {
      if (this.isMobile) return;
      
      const segmentLength = 1 / this.items.length;
      const itemHeight = this.items[0].offsetHeight;
      const windowHeight = this.isHeaderSticky ? window.innerHeight - this.headerHeight : window.innerHeight;
      const translateY = Math.min(250, (itemHeight * 0.3));
      const endpoint = Math.min((itemHeight / windowHeight), 1);
      const cardZoom = 4;

      [...this.items].forEach((item, i) => {
        const index = i + 1;
        const content = item.querySelector('.slide__content');
        const product = item.querySelector('.slide__product');
        const card = item.querySelector('.slide__card');
        const cardMedia = card.querySelector('.media');
        const cardMediaChild = cardMedia.children[0];

        // Set will-change only on desktop
        card.style.willChange = 'clip-path';
        if (content) content.style.willChange = 'transform';
        cardMedia.style.willChange = 'transform';

        // Zoom animations
        if (this.zoom) {
          // Card clip-path animation
          try {
            const zoomAnimation1 = FoxTheme.Motion.scroll(
              FoxTheme.Motion.animate(
                card,
                {
                  clipPath: [
                    `inset(0 round ${this.borderRadius}px)`, 
                    `inset(0 ${cardZoom}% round ${this.borderRadius}px)`
                  ],
                }
              ),
              { 
                target: this, 
                offset: [
                  [(index * segmentLength) + 0.012, endpoint],
                  [(index + 1) * segmentLength, endpoint]
                ] 
              }
            );
            if (zoomAnimation1) this.scrollAnimations.push(zoomAnimation1);
          } catch (e) {
            console.warn('Zoom animation 1 failed:', e);
          }

          // Content scale animation
          try {
            const zoomAnimation2 = FoxTheme.Motion.scroll(
              FoxTheme.Motion.animate(
                content,
                {
                  transform: [`scale(1)`, `scale(${(100 - cardZoom * 2) / 100})`],
                }
              ),
              { 
                target: this, 
                offset: [
                  [(index * segmentLength) + 0.012, endpoint],
                  [(index + 1) * segmentLength, endpoint]
                ] 
              }
            );
            if (zoomAnimation2) this.scrollAnimations.push(zoomAnimation2);
          } catch (e) {
            console.warn('Zoom animation 2 failed:', e);
          }

          // Product animation
          if (product) {
            try {
              const productAnimation = FoxTheme.Motion.scroll(
                FoxTheme.Motion.animate(
                  product,
                  {
                    transform: [`translateY(0)`, `translateY(${itemHeight / 100 * 4}px)`],
                  }
                ),
                { 
                  target: this, 
                  offset: [
                    [(index * segmentLength) + 0.012, endpoint],
                    [(index + 1) * segmentLength, endpoint]
                  ] 
                }
              );
              if (productAnimation) this.scrollAnimations.push(productAnimation);
            } catch (e) {
              console.warn('Product animation failed:', e);
            }
          }
        }

        // Parallax animations
        if (this.parallax) {
          try {
            if (i == 0) {
              const parallaxAnimation1 = FoxTheme.Motion.scroll(
                FoxTheme.Motion.animate(
                  cardMedia,
                  { 
                    transform: [`translateY(0)`, `translateY(${-translateY}px)`], 
                    transformOrigin: ['top', 'top'] 
                  },
                  { easing: "ease-out" }
                ),
                { 
                  target: this, 
                  offset: [
                    [(index * segmentLength), endpoint],
                    [(index + 1) * segmentLength, endpoint]
                  ] 
                }
              );
              if (parallaxAnimation1) this.scrollAnimations.push(parallaxAnimation1);
            } else {
              const parallaxAnimation2 = FoxTheme.Motion.scroll(
                FoxTheme.Motion.animate(
                  cardMedia,
                  { 
                    transform: [`translateY(${-translateY}px)`, `translateY(0)`], 
                    transformOrigin: ['bottom', 'bottom'] 
                  },
                  { easing: "ease-out" }
                ),
                { 
                  target: this, 
                  offset: [
                    [i * segmentLength, 1],
                    [index * segmentLength, 1]
                  ] 
                }
              );
              if (parallaxAnimation2) this.scrollAnimations.push(parallaxAnimation2);

              if (i < this.items.length - 1) {
                const parallaxAnimation3 = FoxTheme.Motion.scroll(
                  FoxTheme.Motion.animate(
                    cardMediaChild,
                    { 
                      transform: [`translateY(0)`, `translateY(${-translateY}px)`], 
                      transformOrigin: ['top', 'top'] 
                    },
                    { easing: "ease-out" }
                  ),
                  { 
                    target: this, 
                    offset: [
                      [i * segmentLength, this.isHeaderSticky ? this.headerHeight/window.innerHeight : 0],
                      [index * segmentLength, this.isHeaderSticky ? this.headerHeight/window.innerHeight : 0]
                    ] 
                  }
                );
                if (parallaxAnimation3) this.scrollAnimations.push(parallaxAnimation3);
              }
            }
          } catch (e) {
            console.warn('Parallax animation failed:', e);
          }
        }
      });

      this.slideRequestAnimationFrame = true;
    }

    disconnectedCallback() {
      if (this.onResizeHandler) {
        window.removeEventListener("resize", this.onResizeHandler);
      }
      this.cleanup();
    }
  });
}